/**
 * Pure selector functions for the NegotiationChatScreen redesign.
 *
 * All functions are stateless, dependency-free, and safe to unit test in
 * isolation. No React imports, no I/O, no side effects.
 *
 * Requirements: 5.2, 5.10, 5.11, 5.12, 5.14, 5.15, 7.1, 7.2, 7.3, 7.4, 7.5, 7.8
 */

import {
  NEGOTIATION_CONFIG,
  RECOGNISED_AUTO_PAUSE_REASONS,
} from '../../config/negotiationConfig';

// ---------------------------------------------------------------------------
// 1. selectActiveDrafts
// ---------------------------------------------------------------------------

/**
 * Filter messages to the set of "active" agent drafts and sort them by a
 * fully-pinned key (round_number ASC, draft_version ASC, created_at ASC).
 * The deterministic sort prevents realtime INSERT/UPDATE races from causing
 * the "latest" draft to flip between two equally-valid candidates.
 *
 * @param {Array} messages - All negotiation_messages for this negotiation.
 * @returns {Array} Sorted active draft messages.
 */
export function selectActiveDrafts(messages) {
  if (!Array.isArray(messages)) return [];

  return messages
    .filter(
      (m) =>
        (m.role === 'agent_draft' ||
          (m.role === 'user_written' && m.status === 'safety_blocked')) &&
        ['draft', 'guardrail_blocked', 'safety_blocked', 'needs_user_input', 'generating'].includes(m.status)
    )
    .sort(
      (a, b) =>
        (a.round_number - b.round_number) ||
        ((a.draft_version ?? 0) - (b.draft_version ?? 0)) ||
        new Date(a.created_at) - new Date(b.created_at)
    );
}

// ---------------------------------------------------------------------------
// 2. selectActionSurface
// ---------------------------------------------------------------------------

/**
 * Derive the single action-surface variant to render in the center column.
 * Priority is fully ordered: terminal states first, then auto-paused, then
 * draft-block states, then idle.
 *
 * Returned values:
 *   'handoff'    — deal_reached or handed_off
 *   'closed'     — closed or abandoned
 *   'paused'     — paused status OR auto_paused with a truthy reason
 *   'guardrail'  — latest active draft is guardrail_blocked
 *   'needs_input'— latest active draft needs_user_input
 *   'generating' — latest active draft is generating
 *   'draft'      — latest active draft is draft (ready to review)
 *   'idle'       — no active drafts and no terminal/paused state
 *
 * @param {Object|null} negotiation - The negotiations row.
 * @param {Array} activeDrafts - Output of selectActiveDrafts().
 * @returns {string} One of the action-surface variant strings above.
 */
export function selectActionSurface(negotiation, activeDrafts) {
  if (!negotiation) return 'idle';

  if (negotiation.status === 'deal_reached' || negotiation.status === 'handed_off') {
    return 'handoff';
  }

  if (negotiation.status === 'closed' || negotiation.status === 'abandoned') {
    return 'closed';
  }

  // Both explicit 'paused' status AND auto_paused-with-a-truthy-reason
  // suppress the draft block. auto_paused with null/empty reason falls through
  // (R5 AC12 explicit fallthrough).
  if (
    negotiation.status === 'paused' ||
    (negotiation.auto_paused === true && !!negotiation.auto_pause_reason)
  ) {
    return 'paused';
  }

  const latest = activeDrafts.at(-1);
  if (!latest) return 'idle';

  if (latest.status === 'guardrail_blocked' || latest.status === 'safety_blocked') return 'guardrail';
  if (latest.status === 'needs_user_input') return 'needs_input';
  if (latest.status === 'generating') return 'generating';
  if (latest.status === 'draft') return 'draft';

  return 'idle';
}

// ---------------------------------------------------------------------------
// 3. buildBannerStack
// ---------------------------------------------------------------------------

/**
 * Determine which informational banners to display above the chat thread,
 * returned as an ordered array (index 0 = highest priority / topmost banner).
 *
 * Each entry is `{ kind: string, props: Object }`. The caller maps `kind` to
 * the matching banner component.
 *
 * Suppression rules (encoded here, not in the caller):
 *   - handoff or closed action surface → no banners at all.
 *
 * Emission order (highest to lowest priority):
 *   1. listing_removed   — auto_paused by listing removal
 *   2. auto_paused_generic — auto_paused for an unrecognised reason
 *   3. paused_user       — user-initiated pause (status === 'paused', not auto_paused)
 *   4. price_drop        — a price_drop event exists
 *   5. stalled           — dealer has not replied within FOLLOW_UP_THRESHOLD_HOURS
 *   6. form_bridge       — guided mode, no dealer reply yet, not dismissed
 *   7. post_call_coaching— guided mode, has outbound messages, no active drafts
 *
 * @param {Object|null} negotiation  - The negotiations row.
 * @param {Array}       events       - All negotiation_events rows.
 * @param {Array}       messages     - All negotiation_messages rows.
 * @param {Object}      dismissals   - Dismissal flags, e.g. { formBridge: true }.
 * @returns {Array<{kind: string, props: Object}>}
 */
export function buildBannerStack(negotiation, events, messages, dismissals = {}) {
  if (!negotiation) return [];

  // Handoff and closed suppress ALL banners.
  const status = negotiation.status;
  if (status === 'deal_reached' || status === 'handed_off') return [];
  if (status === 'closed' || status === 'abandoned') return [];

  const stack = [];
  const safeEvents = Array.isArray(events) ? events : [];
  const safeMessages = Array.isArray(messages) ? messages : [];

  // 1. listing_removed — specific auto-pause reason with dedicated banner.
  if (
    negotiation.auto_paused === true &&
    negotiation.auto_pause_reason === 'listing_removed'
  ) {
    stack.push({ kind: 'listing_removed', props: {} });
  }

  // 2. auto_paused_generic — auto_paused but reason not in RECOGNISED_AUTO_PAUSE_REASONS.
  if (
    negotiation.auto_paused === true &&
    negotiation.auto_pause_reason &&
    !RECOGNISED_AUTO_PAUSE_REASONS.includes(negotiation.auto_pause_reason)
  ) {
    stack.push({ kind: 'auto_paused_generic', props: { reason: negotiation.auto_pause_reason } });
  }

  // 3. paused_user — explicit user pause, not an auto-pause.
  if (negotiation.status === 'paused' && !negotiation.auto_paused) {
    stack.push({ kind: 'paused_user', props: {} });
  }

  // 4. price_drop — use the most recent price_drop event.
  const priceDrop = safeEvents
    .filter((e) => e.event_type === 'price_drop')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  if (priceDrop) {
    stack.push({ kind: 'price_drop', props: { event: priceDrop } });
  }

  // 5. stalled — dealer has not replied within FOLLOW_UP_THRESHOLD_HOURS since
  // the last outbound send. Mirrors the showFollowUpPrompt logic in
  // NegotiationChatScreen.js (lines 585-604).
  //
  // Condition:
  //   - status is 'active' (48h-72h window before the server flips to stalled)
  //     OR status is 'stalled' (server already flipped at 72h; the CTA must
  //     stay visible so the user can still send a follow-up, until the 14d
  //     auto-abandon sweep runs)
  //   - listing_removed auto-pauses also land on status='stalled' but get
  //     their own banner above, so suppress here
  //   - last_message_sent_at is set
  //   - for 'active': the elapsed time exceeds the threshold
  //   - no dealer_received message arrived after last_message_sent_at
  const isStalledCondition = (() => {
    if (!negotiation.last_message_sent_at) return false;
    if (negotiation.status !== 'active' && negotiation.status !== 'stalled') return false;
    if (
      negotiation.status === 'stalled' &&
      negotiation.auto_paused === true &&
      negotiation.auto_pause_reason === 'listing_removed'
    ) return false;

    if (negotiation.status === 'active') {
      const sentAt = new Date(negotiation.last_message_sent_at).getTime();
      const thresholdMs = NEGOTIATION_CONFIG.FOLLOW_UP_THRESHOLD_HOURS * 60 * 60 * 1000;
      if (Date.now() - sentAt <= thresholdMs) return false;
    }

    const sentAtMs = new Date(negotiation.last_message_sent_at).getTime();
    const hasDealerReplySinceSend = safeMessages.some(
      (m) =>
        m.role === 'dealer_received' &&
        new Date(m.created_at).getTime() > sentAtMs
    );

    return !hasDealerReplySinceSend;
  })();

  if (isStalledCondition) {
    stack.push({ kind: 'stalled', props: {} });
  }

  // 6. form_bridge — guided mode, dealer has not replied yet, not dismissed.
  // Mirrors the FormBridgeCard visibility rule in NegotiationChatScreen.js
  // (line 1388). The screen additionally gates on !['deal_reached', 'handed_off',
  // 'abandoned', 'closed'] which is already handled by the early returns above.
  //
  // Audit C2 (2026-04-30): suppress the "Go to the dealer's listing" guide
  // when the listing has been removed. Step 1 of the guide ("Open listing on
  // dealer site") points at a dead URL and contradicts the listing-removed
  // banner above it.
  const hasAnyDealerReply = safeMessages.some((m) => m.role === 'dealer_received');
  const listingRemoved =
    negotiation.auto_paused === true &&
    negotiation.auto_pause_reason === 'listing_removed';
  if (
    negotiation.autonomy === 'guided' &&
    !hasAnyDealerReply &&
    !listingRemoved &&
    dismissals.formBridge !== true
  ) {
    stack.push({ kind: 'form_bridge', props: {} });
  }

  // 7. post_call_coaching — guided mode, has outbound messages but no active
  // drafts. Mirrors the GuidedInput visibility rule in NegotiationChatScreen.js
  // (line 1409). The screen gates on activeDrafts.length === 0, messages.length > 0,
  // waitingForDraft === false, and not terminal; terminal is handled by early
  // returns above. Active-drafts check is deliberately omitted at selector level
  // because the screen will have already computed selectActiveDrafts() — it
  // applies its own guard when mounting GuidedInput. Here we emit the banner
  // whenever any outbound message exists in guided mode so the screen can
  // decide whether to mount based on its draft state.
  //
  // TODO (Task 27b/c): Refine this condition once the screen rewrite is done.
  // Currently emits whenever guided + has outbound + not terminal. The screen
  // suppresses rendering when activeDrafts.length > 0 or waitingForDraft is true.
  // Outbound takes two shapes in guided mode:
  //   1. agent_sent — the worker delivered an email (autonomous email path,
  //      not used in pure script-handoff).
  //   2. agent_draft + status='approved' — script-handoff acknowledgement.
  //      `handleApprove` flips the draft to 'approved' and stops (no
  //      agent_sent row) because the user is the one physically sending.
  // Either is sufficient to mean "the user has acted; show them GuidedInput
  // so they can log what the dealer said."
  const hasOutboundMessage =
    safeMessages.some((m) => m.role === 'agent_sent') ||
    safeMessages.some(
      (m) => m.role === 'agent_draft' && m.status === 'approved'
    );
  if (negotiation.autonomy === 'guided' && hasOutboundMessage) {
    stack.push({ kind: 'post_call_coaching', props: {} });
  }

  return stack;
}
